import invariant from 'react-utils/invariant';
import { propertyLabelTranslator } from 'property-translator/propertyTranslator';
import { PropertyDataSensitivity } from 'customer-data-objects/property/PropertyDataSensitivity';
import I18n from 'I18n';
import { PropertyCategories, SiteSettings, DeprecatedProperties, CategoryOrder, USER_PROPERTY_TYPE } from '../constants/properties';
import { isCustomObjectCategory } from './properties';
import { CUSTOM_EVENT_CATEGORY } from 'data-token-picker/constants/Properties';
const i18nKey = (suffix, params = {}) => I18n.text(`RichTextLib.personalize.${suffix}`, params);

// wrapped in a function to defer calling property-translator/I18n until necessary
const getSiteSettingsOption = () => {
  const siteSettingsProperties = SiteSettings.map(setting => ({
    text: propertyLabelTranslator(setting),
    value: `site_settings.${setting}`,
    name: setting,
    category: 'site_settings',
    isHubSpotDefined: true
  }));
  return {
    help: i18nKey('help.categories.site_settings'),
    options: siteSettingsProperties,
    text: i18nKey('categories.site_settings'),
    value: 'site_settings'
  };
};
const getSubscriptionTypeOption = () => {
  const subscriptionTypePropertyName = 'subscription_name';
  const subscriptionTypeProperties = [{
    text: i18nKey('categories.subscription_type'),
    value: `subscription_type.${subscriptionTypePropertyName}`,
    name: subscriptionTypePropertyName,
    category: 'subscription_type',
    isHubSpotDefined: true
  }];
  return {
    help: i18nKey('help.categories.subscription_type'),
    options: subscriptionTypeProperties,
    text: i18nKey('categories.subscription_type'),
    value: 'subscription_type'
  };
};
const getStaticCategories = personalizeConfig => {
  const {
    hideSiteSettingsCategory,
    includeSubscriptionTypeCategory
  } = personalizeConfig;
  const supportedStaticCategories = [];
  if (!hideSiteSettingsCategory) {
    supportedStaticCategories.push(getSiteSettingsOption());
  }
  if (includeSubscriptionTypeCategory) {
    supportedStaticCategories.push(getSubscriptionTypeOption());
  }
  return supportedStaticCategories;
};
const INTERNAL_CONTACT_RECORD = {
  property: {
    label: 'Internal Contact Record',
    name: 'hubspot_contact_record'
  }
};
const SelectOptionsCache = {};

// create a UISelect-friendly `options` array for the given category
export const createSelectOptionsForCategory = (category, properties, invalidateCache = false) => {
  if (SelectOptionsCache[category] && !invalidateCache) {
    return SelectOptionsCache[category];
  } else {
    invariant(Array.isArray(properties), 'properties must be an Array');
    const processedProperties = properties.reduce((currentlyProcessedProperties, {
      property
    }) => {
      if (!property.hidden && !DeprecatedProperties.includes(property.name)) {
        // propertyLabelTranslator should only be used for hubspot defined properties:
        // https://git.hubteam.com/HubSpot/property-translator#propertylabeltranslator
        const isHubSpotDefined = !!property.hubspotDefined;
        if (isHubSpotDefined) {
          const {
            label = ''
          } = property;
          property.label = propertyLabelTranslator(label);
        }

        // All properties should default to 'none'
        const disabled = property.dataSensitivity === PropertyDataSensitivity.STANDARD || property.dataSensitivity === PropertyDataSensitivity.HIGH;
        currentlyProcessedProperties.push({
          text: property.label,
          value: `${category}.${property.name}`,
          name: property.name,
          referencedObjectType: property.referencedObjectType,
          category,
          isHubSpotDefined,
          fieldType: property.fieldType,
          originalProperty: property,
          disabled,
          disabledTooltip: disabled ? I18n.text('RichTextLib.personalize.dataSensitivityTooltip') : undefined
        });
      }
      return currentlyProcessedProperties;
    }, []);
    processedProperties.sort(({
      text: textA = ''
    }, {
      text: textB = ''
    }) => {
      return textA > textB ? 1 : -1;
    });
    SelectOptionsCache[category] = processedProperties;
    return processedProperties;
  }
};
const PropertiesPromiseCache = {};
export const asCacheKey = (personalizeConfig = {}) => {
  const supportedCrmObjectFQNs = personalizeConfig.supportedCrmObjectFQNs || [];
  const dataTokenContext = personalizeConfig.dataTokenContext || {};
  return Object.keys(dataTokenContext).length > 0 ? JSON.stringify(personalizeConfig.dataTokenContext) : supportedCrmObjectFQNs.join(',');
};
export const getHandleProperties = (fetchProperties, invalidateCache = false, personalizeConfig) => {
  return function handleProperties() {
    const {
      includeInternalContactRecord,
      useFlexibleAssociations = false
    } = personalizeConfig;
    const cacheKey = asCacheKey(personalizeConfig);
    if (Object.prototype.hasOwnProperty.call(PropertiesPromiseCache, cacheKey) && !invalidateCache) {
      return PropertiesPromiseCache[cacheKey];
    }
    const propertiesResponse = fetchProperties(personalizeConfig);
    const pendingCategories = Promise.resolve(propertiesResponse).then(propertyData => {
      return propertyData.map(propertyDataValue => {
        const {
          fqn: category,
          propertiesForCategory,
          customObjectLabels
        } = propertyDataValue;
        if (category === PropertyCategories.CONTACT && includeInternalContactRecord) {
          propertiesForCategory.push(INTERNAL_CONTACT_RECORD);
        }
        const propertyOptions = createSelectOptionsForCategory(category, propertiesForCategory, invalidateCache);
        if (!isCustomObjectCategory(category)) {
          let categoryLabelKey = category;
          if (category === PropertyCategories.COMPANY && useFlexibleAssociations) {
            categoryLabelKey = PropertyCategories.PRIMARY_COMPANY;
          }
          return {
            text: i18nKey(`categories.${categoryLabelKey}`),
            help: i18nKey(`help.categories.${categoryLabelKey}`),
            options: propertyOptions,
            value: category
          };
        }
        const customObjectLabel = customObjectLabels[category] || category;
        return {
          text: customObjectLabel,
          help: i18nKey('help.categories.customObject', {
            translatedCategory: customObjectLabel
          }),
          options: propertyOptions,
          value: category
        };
      });
    });
    PropertiesPromiseCache[cacheKey] = Promise.resolve(pendingCategories).then(fetchedCategories => {
      const staticCategories = getStaticCategories(personalizeConfig);
      const allCategories = fetchedCategories.concat(staticCategories);
      return allCategories.sort((a, b) => {
        const aIndex = CategoryOrder.indexOf(a.value);
        const bIndex = CategoryOrder.indexOf(b.value);
        if (aIndex < 0 && bIndex < 0) {
          // non-HubSpot-defined (i.e. custom) objects, leave them in the order they were passed
          return 0;
        }
        if (aIndex < 0) {
          // put any custom object after a HubSpot-defined object
          return 1;
        }
        if (bIndex < 0) {
          // put any custom object after a HubSpot-defined object
          return -1;
        }
        return aIndex - bIndex;
      });
    });
    return PropertiesPromiseCache[cacheKey];
  };
};
const PropertiesLabelsMapCache = {};
const PropertiesDataSensitivityMapCache = {};
export const getPropertiesLabelsMap = personalizeConfig => {
  const cacheKey = asCacheKey(personalizeConfig);
  return PropertiesLabelsMapCache[cacheKey];
};
export const setPropertiesLabelsMap = (newPropertiesLabelsMap, personalizeConfig) => {
  const cacheKey = asCacheKey(personalizeConfig);
  PropertiesLabelsMapCache[cacheKey] = newPropertiesLabelsMap;
};
export const convertToPropertyLabelMap = propertyData => {
  return propertyData.reduce((labelMap, categoryData) => {
    if (categoryData) {
      categoryData.options.forEach(({
        value = '',
        text = ''
      }) => {
        labelMap[value] = text;
      });
    }
    return labelMap;
  }, {});
};
export const getPropertiesDataSensitivityMap = personalizeConfig => {
  const cacheKey = asCacheKey(personalizeConfig);
  return PropertiesDataSensitivityMapCache[cacheKey];
};
export const setPropertiesDataSensitivityMap = (newPropertiesDataSensitivityMap, personalizeConfig) => {
  const cacheKey = asCacheKey(personalizeConfig);
  PropertiesDataSensitivityMapCache[cacheKey] = newPropertiesDataSensitivityMap;
};
export const convertToPropertyDataSensitivityMap = propertyData => {
  return propertyData.reduce((dataSensitivityMap, categoryData) => {
    if (categoryData) {
      categoryData.options.forEach(({
        value = '',
        originalProperty = {}
      }) => {
        if (originalProperty.dataSensitivity === PropertyDataSensitivity.STANDARD || originalProperty.dataSensitivity === PropertyDataSensitivity.HIGH) {
          dataSensitivityMap[value] = true;
        }
      });
    }
    return dataSensitivityMap;
  }, {});
};

// Create PropertiesLabelMap and cache it given the set of supportedCrmObjectFQNs or paths context
export const createPropertiesLabelsMap = (fetchProperties, personalizeConfig, invalidateCache) => {
  const handleProperties = getHandleProperties(fetchProperties, invalidateCache, personalizeConfig);
  return handleProperties().then(propertyData => {
    const propertiesLabelMap = convertToPropertyLabelMap(propertyData);
    setPropertiesLabelsMap(propertiesLabelMap, personalizeConfig);
    const propertiesDataSensitivityMap = convertToPropertyDataSensitivityMap(propertyData);
    setPropertiesDataSensitivityMap(propertiesDataSensitivityMap, personalizeConfig);
    return Promise.resolve(propertiesLabelMap);
  });
};
const labelsMapWalker = (node, map) => {
  if (node.path && node.nodeType === 'FIELD') {
    const adjustedLabel = node.parentLabels ? [...node.parentLabels, node.label].join(' > ') : node.label;
    map[node.path] = adjustedLabel;
  }
  if (node.childNodes) {
    node.childNodes.forEach(child => labelsMapWalker(child, map));
  }
};

// Builds path labels cache for data token functionality
const buildPathLabelsCache = (personalizeConfig, paths) => {
  const propertiesLabelMap = {};
  const dataSources = paths.dataTokenNodes;
  dataSources.forEach(dataSource => labelsMapWalker(dataSource, propertiesLabelMap));
  setPropertiesLabelsMap(propertiesLabelMap, personalizeConfig);
};
export const getPathsCacheKey = personalizeConfig => {
  const {
    dataTokenContext,
    supportedCrmObjectFQNs = [],
    hideSiteSettingsCategory,
    includeSubscriptionTypeCategory,
    hideFallbacks,
    isEmail
  } = personalizeConfig;
  return JSON.stringify([dataTokenContext, supportedCrmObjectFQNs, hideSiteSettingsCategory, includeSubscriptionTypeCategory, hideFallbacks, isEmail]);
};
const BrowserCategoriesCache = {};
export const getBrowserCategoriesCache = personalizeConfig => {
  return BrowserCategoriesCache[getPathsCacheKey(personalizeConfig)];
};
const BrowserFieldsCache = {};
export const getBrowserFieldsCache = personalizeConfig => {
  return BrowserFieldsCache[getPathsCacheKey(personalizeConfig)];
};
const BrowserEventsCache = {};
export const getBrowserEventsCache = personalizeConfig => {
  return BrowserEventsCache[getPathsCacheKey(personalizeConfig)] || [];
};
const alphabeticalFieldOptionsCompare = (a, b) => {
  const textA = a.text.toLowerCase();
  const textB = b.text.toLowerCase();
  if (textA < textB) {
    return -1;
  }
  if (textA > textB) {
    return 1;
  }
  return 0;
};
const categoriesAsOptions = node => {
  const {
    label,
    description,
    path,
    childNodes
  } = node;
  let options;
  let help;
  const hasChildren = childNodes && childNodes.length;
  if (hasChildren && childNodes[0].nodeType !== 'FIELD' && path !== CUSTOM_EVENT_CATEGORY) {
    options = childNodes.map(categoriesAsOptions);
  } else {
    help = description;
  }
  if (!path && hasChildren && childNodes[0].nodeType === 'FIELD') {
    return {
      text: label,
      value: 'property-only',
      help,
      options
    };
  }
  return {
    text: label,
    value: path,
    help,
    options
  };
};
const fieldsAsOptions = (node, parentNode) => {
  const {
    label,
    fieldType,
    hubspotDefined,
    path = '',
    childNodes,
    fieldDefaultValue,
    parentFieldDefaultValue,
    parentLabels,
    dataSensitivity
  } = node;
  let children;
  if (childNodes && childNodes.length) {
    children = childNodes.map(childNode => fieldsAsOptions(childNode, node)).sort(alphabeticalFieldOptionsCompare);
  }
  const _dataSensitivity = dataSensitivity || parentNode && parentNode.dataSensitivity;

  // All properties should default to 'none'
  const disabled = _dataSensitivity === PropertyDataSensitivity.STANDARD || _dataSensitivity === PropertyDataSensitivity.HIGH;
  const adjustedLabel = parentLabels ? [...parentLabels, label].join(' > ') : label;
  return {
    text: adjustedLabel,
    value: path,
    fieldType,
    isOwnerField: parentNode && parentNode.referencedType === USER_PROPERTY_TYPE,
    hubspotDefined,
    children,
    fieldDefaultValue,
    parentFieldDefaultValue,
    disabled,
    disabledTooltip: disabled ? I18n.text('RichTextLib.personalize.dataSensitivityTooltip') : undefined
  };
};
const buildFieldOptions = (byCategory, node) => {
  const {
    childNodes,
    path = 'property-only'
  } = node;
  if (childNodes && childNodes.length) {
    if (childNodes[0].nodeType === 'FIELD') {
      byCategory[path] = childNodes.map(childNode => fieldsAsOptions(childNode)).sort(alphabeticalFieldOptionsCompare);
    } else {
      // For custom_event node, store each event's fields under 'custom_event.event_name'
      if (path === CUSTOM_EVENT_CATEGORY) {
        childNodes.forEach(eventNode => {
          if (eventNode.name && eventNode.childNodes) {
            const eventKey = `${path}.${eventNode.name}`;
            byCategory[eventKey] = eventNode.childNodes.map(fieldNode => fieldsAsOptions(fieldNode)).sort(alphabeticalFieldOptionsCompare);
          }
        });
      }
      childNodes.forEach(childCategory => buildFieldOptions(byCategory, childCategory));
    }
  }
  return byCategory;
};
const buildBrowserPathsCache = (personalizeConfig, paths) => {
  const cacheKey = getPathsCacheKey(personalizeConfig);

  // Extract custom_event node to build events separately
  const customEventNode = paths.dataTokenNodes.find(node => node.path === CUSTOM_EVENT_CATEGORY);
  if (customEventNode && customEventNode.childNodes) {
    BrowserEventsCache[cacheKey] = customEventNode.childNodes.map(childNode => {
      const eventOption = fieldsAsOptions(childNode);
      return Object.assign({}, eventOption, {
        value: childNode.name || eventOption.value,
        help: childNode.description || eventOption.help
      });
    }).filter(event => event.value).sort(alphabeticalFieldOptionsCompare);
  } else {
    BrowserEventsCache[cacheKey] = [];
  }
  BrowserCategoriesCache[cacheKey] = paths.dataTokenNodes.map(categoriesAsOptions);
  BrowserFieldsCache[cacheKey] = paths.dataTokenNodes.reduce(buildFieldOptions, {});
};
const PathsMapCache = {};
export const getPathsMap = personalizeConfig => {
  const cacheKey = getPathsCacheKey(personalizeConfig);
  return PathsMapCache[cacheKey];
};
const setPathsMap = (newPathsMap, personalizeConfig) => {
  const cacheKey = getPathsCacheKey(personalizeConfig);
  PathsMapCache[cacheKey] = newPathsMap;
};
const pathMapWalker = (node, map, category) => {
  if (node.path && node.nodeType === 'FIELD') {
    // All properties should default to 'none'
    const hasDataSensitivity = node.dataSensitivity === PropertyDataSensitivity.STANDARD || node.dataSensitivity === PropertyDataSensitivity.HIGH;
    const field = category ? node.path.replace(`${category}.`, '') : node.path;
    map[node.path] = {
      label: node.parentLabels ? [...node.parentLabels, node.label].join(' > ') : node.label,
      path: node.path,
      category: category || 'property-only',
      field,
      hasDataSensitivity
    };
  }
  if (node.childNodes) {
    const localCategory = category || node.path;
    node.childNodes.forEach(child => pathMapWalker(child, map, localCategory));
  }
};
const buildPathsCache = (personalizeConfig, paths) => {
  const pathsMap = {};
  const dataSources = paths.dataTokenNodes;
  dataSources.forEach(dataSource => pathMapWalker(dataSource, pathsMap));
  setPathsMap(pathsMap, personalizeConfig);
};
export const buildPaths = (personalizeConfig, paths) => {
  if (!paths) return;
  buildPathLabelsCache(personalizeConfig, paths);
  buildPathsCache(personalizeConfig, paths);
  buildBrowserPathsCache(personalizeConfig, paths);
};